// The suitability refresh of an existing client (SEP-288 sections 3 to 5).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    RefreshChangeSummaryDto,
    RefreshDecisionRequest,
    SaveRefreshChecklistRequest,
    SuitabilityRefreshDto,
} from '@septima/contract';
import { api, apiFetch, staffErrorMessage } from '@/lib/api';
import { downloadBlob } from '@/lib/download';

/** The feature that owns the suitability refresh (SEP-288). */
export const SUITABILITY_REFRESH_FEATURE = 'dowgate.onboarding.method';

export const suitabilityRefreshKeys = {
    all: ['suitability-refresh'] as const,
    byVersion: (versionId: string) => ['suitability-refresh', 'version', versionId] as const,
    queue: (status?: string) => ['suitability-refresh', 'queue', status ?? null] as const,
    // Under the fact-find key, so each save of an answer reads the changes again.
    changeSummary: (refreshId: string) =>
        ['fact-find', 'refresh-change-summary', refreshId] as const,
};

/** The refresh of one SUI, or null when the SUI is not a refresh. */
export function useRefreshByVersion(versionId: string | undefined) {
    return useQuery({
        queryKey: suitabilityRefreshKeys.byVersion(versionId ?? ''),
        enabled: Boolean(versionId),
        queryFn: async (): Promise<SuitabilityRefreshDto | null> => {
            const res = await api.suitabilityRefresh.getRefreshByVersion({
                params: { versionId: versionId ?? '' },
            });
            if (res.status !== 200) throw new Error(`Refresh failed: ${res.status}`);
            return res.body.refresh;
        },
        staleTime: 10_000,
    });
}

/** The refreshes that wait for Compliance. */
export function useRefreshQueue(status?: string) {
    return useQuery({
        queryKey: suitabilityRefreshKeys.queue(status),
        queryFn: async (): Promise<SuitabilityRefreshDto[]> => {
            const res = await api.suitabilityRefresh.listRefreshQueue({
                query: status ? { status } : {},
            });
            if (res.status !== 200) throw new Error(`Refresh queue failed: ${res.status}`);
            return res.body.rows;
        },
        staleTime: 10_000,
    });
}

/** One write of a refresh. Each write refreshes the refresh, the SUI lists and the fact find. */
function useRefreshWrite<I>(call: (input: I) => Promise<{ status: number; body: unknown }>) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: I): Promise<SuitabilityRefreshDto> => {
            const res = await call(input);
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return (res.body as { refresh: SuitabilityRefreshDto }).refresh;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: suitabilityRefreshKeys.all });
            void qc.invalidateQueries({ queryKey: ['suis'] });
            void qc.invalidateQueries({ queryKey: ['fact-find'] });
        },
    });
}

export function useMarkRefreshIssued() {
    return useRefreshWrite((input: { refreshId: string }) =>
        api.suitabilityRefresh.markRefreshIssued({ params: input, body: {} }),
    );
}

export function useSaveRefreshChecklist() {
    return useRefreshWrite((input: { refreshId: string; body: SaveRefreshChecklistRequest }) =>
        api.suitabilityRefresh.saveRefreshChecklist({
            params: { refreshId: input.refreshId },
            body: input.body,
        }),
    );
}

export function useSubmitRefreshOutcome() {
    return useRefreshWrite((input: { refreshId: string; body: SaveRefreshChecklistRequest }) =>
        api.suitabilityRefresh.submitRefreshOutcome({
            params: { refreshId: input.refreshId },
            body: input.body,
        }),
    );
}

export function useDecideRefresh() {
    return useRefreshWrite((input: { refreshId: string; body: RefreshDecisionRequest }) =>
        api.suitabilityRefresh.decideRefresh({
            params: { refreshId: input.refreshId },
            body: input.body,
        }),
    );
}

export function useRecordRefreshMandate() {
    return useRefreshWrite((input: { refreshId: string; mandateLetterId: string }) =>
        api.suitabilityRefresh.recordRefreshMandate({
            params: { refreshId: input.refreshId },
            body: { mandateLetterId: input.mandateLetterId },
        }),
    );
}

export function useDecideRefreshMandate() {
    return useRefreshWrite((input: { refreshId: string; body: RefreshDecisionRequest }) =>
        api.suitabilityRefresh.decideRefreshMandate({
            params: { refreshId: input.refreshId },
            body: input.body,
        }),
    );
}

// --- The confirmation letter of Outcome A (SEP-288 section 5) ---

export function useDraftRefreshLetter() {
    return useRefreshWrite((input: { refreshId: string }) =>
        api.suitabilityRefresh.draftRefreshLetter({ params: input, body: {} }),
    );
}

export function useSaveRefreshLetter() {
    return useRefreshWrite((input: { refreshId: string; letterBody: string }) =>
        api.suitabilityRefresh.saveRefreshLetter({
            params: { refreshId: input.refreshId },
            body: { letterBody: input.letterBody },
        }),
    );
}

export function useMarkRefreshLetterSent() {
    return useRefreshWrite((input: { refreshId: string; sentTo: string }) =>
        api.suitabilityRefresh.markRefreshLetterSent({
            params: { refreshId: input.refreshId },
            body: { sentTo: input.sentTo },
        }),
    );
}

/** An Outlook draft of the approved letter. It sends nothing. */
export function useRefreshLetterOutlookDraft() {
    return useMutation({
        mutationFn: async (input: { refreshId: string }) => {
            const res = await api.suitabilityRefresh.refreshLetterOutlookDraft({
                params: input,
                body: {},
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body;
        },
    });
}

/** Download the saved letter as a Word file on the letterhead. */
export async function downloadRefreshLetterDocx(
    refreshId: string,
    suiReference: string | null,
): Promise<void> {
    const res = await apiFetch(
        `/api/suitability-refresh/${encodeURIComponent(refreshId)}/letter/docx`,
    );
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    downloadBlob(await res.blob(), `suitability-refresh-letter-${suiReference ?? refreshId}.docx`);
}

// ---------------------------------------------------------------------------
// The change summary of the new fact find (SEP-351)
//
// Two halves: the query reads the changed answers and the newest SCRIVENER
// summary, and the mutation QUEUES a `fact_find_change_summary` AgentJob (202
// and the job id). The caller polls the query while a job is in flight.
// ---------------------------------------------------------------------------

/** The changed answers of a refresh fact find, or null when it replaces no SUI. */
export function useRefreshChangeSummary(refreshId: string | undefined, poll = false) {
    return useQuery({
        queryKey: suitabilityRefreshKeys.changeSummary(refreshId ?? ''),
        enabled: Boolean(refreshId),
        queryFn: async (): Promise<RefreshChangeSummaryDto | null> => {
            const res = await api.suitabilityRefresh.getRefreshChangeSummary({
                params: { refreshId: refreshId ?? '' },
            });
            if (res.status !== 200) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.changeSummary;
        },
        refetchInterval: poll ? 3_000 : false,
        staleTime: 5_000,
    });
}

/** Queue one SCRIVENER change summary. Resolves with the AgentJob id. */
export function useRunRefreshChangeSummary() {
    return useMutation({
        mutationFn: async (refreshId: string): Promise<string> => {
            const res = await api.suitabilityRefresh.runRefreshChangeSummary({
                params: { refreshId },
                body: {},
            });
            if (res.status !== 202) throw new Error(staffErrorMessage(res.body, res.status));
            return res.body.jobId;
        },
    });
}
